import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertCircle, FiArrowRight } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const CODE_LENGTH = 7;

export default function CandidateLogin() {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const { candidateLogin } = useAuth();
  const navigate = useNavigate();
  const inputs = useRef([]);

  // Prevent browser back button from working
  useEffect(() => {
    // Push a dummy state to the history stack
    window.history.pushState(null, null, window.location.href);

    const handlePopState = () => {
      // Push again to prevent actual back navigation
      window.history.pushState(null, null, window.location.href);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => { inputs.current[0]?.focus(); }, []);

  const digits = Array.from({ length: CODE_LENGTH }, (_, i) => password[i] || '');
  const complete = password.length === CODE_LENGTH;

  const fail = (message) => {
    setError(message);
    setShake(true);
    setTimeout(() => setShake(false), 450);
  };

  const setDigit = (index, value) => {
    const clean = value.replace(/\D/g, '');
    if (!clean) return;
    // One digit replaces that box. Autofill of several digits fills the following boxes.
    const next = (password.slice(0, index) + clean + password.slice(index + clean.length)).slice(0, CODE_LENGTH);
    setPassword(next);
    setError('');
    inputs.current[Math.min(next.length, CODE_LENGTH - 1)]?.focus();
  };

  const onKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[index]) setPassword(password.slice(0, index) + password.slice(index + 1));
      else if (index > 0) { setPassword(password.slice(0, index - 1) + password.slice(index)); inputs.current[index - 1]?.focus(); }
      setError('');
    } else if (e.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
    else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) inputs.current[index + 1]?.focus();
  };

  const onPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    setPassword(pasted);
    setError('');
    inputs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!complete || loading) return;
    setError('');
    setLoading(true);
    try {
      const res = await candidateLogin(password.trim());
      if (res.candidate.status === 'completed') {
        fail('This assessment has already been submitted.');
        return;
      }
      navigate('/register', { replace: true });
    } catch (err) {
      fail(err.response?.data?.message || 'Invalid access code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ca-page">
      <style>{CSS}</style>

      <aside className="ca-brand">
        <div className="ca-brand-top">
          <span className="ca-logo"><img src="/logo.png" alt="UAC Foods Logo" /></span>
        </div>
        <div className="ca-brand-copy">
          <span className="ca-eyebrow">Candidate assessment</span>
          <h1>UAC Foods<br />Sales Leadership<br />Assessment</h1>
          <p>A proprietary talent evaluation framework designed to identify top-tier commercial leadership across Nigeria's most competitive FMCG markets.</p>
        </div>
        <blockquote>"Calibrated for strategic thinking, commercial acumen, and organizational leadership."</blockquote>
      </aside>

      <main className="ca-main">
        <div className="ca-card">
          <span className="ca-step">Step 1 of 2 · Access</span>
          <h2>Candidate access</h2>
          <p className="ca-sub">Enter the 7-digit access code provided to you by the assessment administrator.</p>

          <div className="ca-error" role="alert" aria-live="assertive" hidden={!error}>
            {error && <><FiAlertCircle size={17} /><span>{error}</span></>}
          </div>

          <form onSubmit={handleSubmit} className="ca-form">
            <fieldset className="ca-fieldset">
              <legend>Access code</legend>
              <div className={`ca-code ${shake ? 'shake' : ''}`} onPaste={onPaste}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => { inputs.current[i] = el; }}
                    className={`${d ? 'filled' : ''}${error ? ' bad' : ''}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete={i === 0 ? 'one-time-code' : 'off'}
                    pattern="[0-9]*"
                    maxLength={i === 0 ? CODE_LENGTH : 1}
                    value={d}
                    onChange={(e) => setDigit(i, e.target.value)}
                    onKeyDown={(e) => onKeyDown(i, e)}
                    onFocus={(e) => { if (i > password.length) inputs.current[password.length]?.focus(); else e.target.select(); }}
                    aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
                    disabled={loading}
                  />
                ))}
              </div>
              <span className="ca-hint">{complete ? 'Code complete. Press Continue.' : `${CODE_LENGTH - password.length} digit${CODE_LENGTH - password.length === 1 ? '' : 's'} to go. You can paste the code.`}</span>
            </fieldset>

            <button className="ca-submit" type="submit" disabled={loading || !complete}>
              {loading ? <><span className="ca-spinner" /> Verifying…</> : <>Continue <FiArrowRight size={18} /></>}
            </button>
          </form>

          <div className="ca-next">
            <b>What happens next</b>
            <p>After your code is verified you will register your details before starting the timed session. Use a stable internet connection and a quiet place.</p>
          </div>

          <p className="ca-footer">© {new Date().getFullYear()} UAC Foods Limited — Proprietary Assessment Tool.</p>
        </div>
      </main>
    </div>
  );
}

/* Scoped styles. This page no longer imports CandidateLogin.module.css */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
.ca-page { --navy:#0a2540; --navy-2:#12365c; --red:#c8102e; --ink:#0f172a; --ink-2:#475569; --ink-3:#94a3b8; --line:#d9e2ec;
  min-height:100vh; min-height:100dvh; display:flex; flex-direction:column; background:#f3f6fa; color:var(--ink); font-family:'Plus Jakarta Sans',ui-sans-serif,system-ui,sans-serif; -webkit-font-smoothing:antialiased; }
.ca-page *, .ca-page *::before, .ca-page *::after { box-sizing:border-box; }
.ca-page button, .ca-page input { font-family:inherit; }
.ca-page :focus-visible { outline:3px solid rgba(200,16,46,.28); outline-offset:2px; border-radius:10px; }
@keyframes ca-spin { to { transform:rotate(360deg); } }
@keyframes ca-rise { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }
@keyframes ca-shake { 10%,90% { transform:translateX(-1px); } 20%,80% { transform:translateX(2px); } 30%,50%,70% { transform:translateX(-5px); } 40%,60% { transform:translateX(5px); } }

.ca-brand { position:relative; display:flex; flex-direction:column; gap:14px; padding:20px 20px 46px; color:#fff; overflow:hidden;
  background:radial-gradient(110% 90% at 100% 0%, rgba(200,16,46,.38), transparent 55%), radial-gradient(90% 80% at 0% 100%, rgba(59,130,246,.22), transparent 55%), linear-gradient(160deg, var(--navy) 0%, var(--navy-2) 100%); }
.ca-brand::after { content:''; position:absolute; inset:0; background-image:linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px); background-size:44px 44px; mask-image:linear-gradient(to bottom, rgba(0,0,0,.7), transparent); pointer-events:none; }
.ca-brand > * { position:relative; z-index:1; }
.ca-logo { display:inline-flex; padding:0; border-radius:0; background:transparent; }
.ca-logo img { display:block; height:30px; width:auto; object-fit:contain; }
.ca-eyebrow { display:inline-block; margin-bottom:7px; font-size:10px; font-weight:800; letter-spacing:.12em; text-transform:uppercase; color:rgba(255,255,255,.7); }
.ca-brand-copy h1 { margin:0; font-size:20px; line-height:1.15; font-weight:800; letter-spacing:-.03em; }
.ca-brand-copy p, .ca-brand blockquote { display:none; }

.ca-main { flex:1; display:flex; align-items:flex-start; justify-content:center; padding:0 14px 28px; }
.ca-card { position:relative; z-index:2; width:100%; max-width:460px; margin-top:-24px; padding:20px 16px 16px; border:1px solid rgba(15,23,42,.07); border-radius:20px; background:#fff; box-shadow:0 18px 42px rgba(15,23,42,.13); animation:ca-rise .4s ease both; }
.ca-step { display:inline-block; padding:4px 9px; border-radius:99px; background:#fdf0f2; color:var(--red); font-size:10px; font-weight:700; }
.ca-card h2 { margin:9px 0 0; font-size:20px; font-weight:800; letter-spacing:-.03em; line-height:1.15; }
.ca-sub { margin:7px 0 14px; font-size:12px; line-height:1.55; color:var(--ink-2); }

.ca-error { display:flex; align-items:flex-start; gap:7px; margin-bottom:10px; padding:9px 11px; border:1px solid #fecaca; border-radius:12px; background:#fef2f2; color:#991b1b; font-size:11.5px; font-weight:600; line-height:1.45; }
.ca-error[hidden] { display:none; }
.ca-error svg { flex:0 0 auto; margin-top:1px; }

.ca-form { display:grid; gap:14px; }
.ca-fieldset { margin:0; padding:0; border:0; min-width:0; }
.ca-fieldset legend { margin-bottom:8px; padding:0; font-size:11px; font-weight:700; color:#334155; }
.ca-code { display:grid; grid-template-columns:repeat(7, minmax(0, 1fr)); gap:6px; }
.ca-code.shake { animation:ca-shake .45s ease both; }
.ca-code input { width:100%; min-width:0; height:46px; padding:0; border:1.5px solid var(--line); border-radius:10px; background:#fff; color:var(--ink); font-size:19px; font-weight:800; text-align:center; outline:none; caret-color:var(--red); transition:border-color .15s, box-shadow .15s, background .15s; }
.ca-code input.filled { border-color:var(--navy); background:#f5f8fc; }
.ca-code input:focus { border-color:var(--red); box-shadow:0 0 0 4px rgba(200,16,46,.12); background:#fff; }
.ca-code input.bad { border-color:#ef4444; }
.ca-code input:disabled { opacity:.65; }
.ca-hint { display:block; margin-top:8px; font-size:10.5px; color:var(--ink-2); }
.ca-submit { display:flex; align-items:center; justify-content:center; gap:7px; width:100%; min-height:42px; border:0; border-radius:11px; background:var(--navy); color:#fff; font-size:12.5px; font-weight:800; cursor:pointer; box-shadow:0 7px 16px rgba(10,37,64,.2); transition:background .2s, transform .15s; }
.ca-submit:hover:not(:disabled) { background:var(--red); transform:translateY(-1px); }
.ca-submit:disabled { opacity:.5; cursor:not-allowed; box-shadow:none; }
.ca-spinner { width:15px; height:15px; border:2px solid rgba(255,255,255,.4); border-top-color:#fff; border-radius:50%; animation:ca-spin .7s linear infinite; }
.ca-next { margin-top:14px; padding:10px 11px; border-radius:11px; background:#f5f8fc; border:1px solid #e1e9f2; }
.ca-next b { display:block; margin-bottom:4px; font-size:11px; }
.ca-next p { margin:0; font-size:10.5px; line-height:1.55; color:var(--ink-2); }
.ca-footer { margin:14px 0 0; text-align:center; font-size:10px; color:var(--ink-3); }

@media (max-width:380px) { .ca-code { gap:5px; } .ca-code input { height:44px; font-size:17px; border-radius:9px; } }
@media (min-width:640px) { .ca-card { padding:24px 24px 20px; } }
@media (min-width:900px) {
  .ca-page { flex-direction:row; }
  .ca-brand { flex:1 1 54%; justify-content:space-between; gap:0; padding:44px 56px; }
  .ca-logo img { height:38px; }
  .ca-brand-copy h1 { font-size:clamp(27px, 2.7vw, 38px); }
  .ca-brand-copy p { display:block; margin:12px 0 0; max-width:480px; font-size:12.5px; line-height:1.65; color:rgba(255,255,255,.78); }
  .ca-brand blockquote { display:block; margin:0; padding-left:12px; border-left:3px solid var(--red); font-size:11.5px; font-style:italic; line-height:1.6; color:rgba(255,255,255,.75); }
  .ca-main { flex:1 1 46%; align-items:center; padding:28px; background:#fff; }
  .ca-card { margin-top:0; padding:8px; border:0; border-radius:0; box-shadow:none; }
  .ca-card h2 { font-size:24px; }
}
@media (prefers-reduced-motion:reduce) { .ca-page *, .ca-card { animation:none !important; transition:none !important; } }
`;